import PropTypes from "prop-types";
import { useEffect, useState } from "react";

import DashboardShell from "../components/common/DashboardShell";
import { LeaderboardApi } from "../utils/leaderboardApi";

const TABS = [
  { key: "developers", label: "Developers" },
  { key: "teams", label: "Teams" },
  { key: "chapters", label: "Chapters" },
];

const TIER_STYLES = {
  bronze:
    "bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300",
  silver: "bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200",
  gold: "bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300",
  platinum: "bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300",
};

function TierBadge({ tier, label }) {
  if (!tier) return null;
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
        TIER_STYLES[tier] ||
        "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
      }`}
    >
      {label || tier}
    </span>
  );
}

TierBadge.propTypes = {
  tier: PropTypes.string,
  label: PropTypes.string,
};

export default function Leaderboard() {
  const [activeTab, setActiveTab] = useState("developers");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    LeaderboardApi.fetch(activeTab)
      .then((data) => {
        if (!cancelled) setRows(data);
        return null;
      })
      .catch(() => {
        if (!cancelled) {
          setRows([]);
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  return (
    <DashboardShell>
      <div className="mx-auto max-w-3xl p-6">
        <h1 className="mb-4 text-2xl font-bold text-gray-800 dark:text-gray-100">
          League Leaderboard
        </h1>

        <div className="mb-4 flex gap-2 border-b dark:border-gray-700">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === tab.key
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
              type="button"
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-2 py-4">
            {["s1", "s2", "s3", "s4", "s5"].map((key) => (
              <div
                key={key}
                className="h-8 animate-pulse rounded bg-gray-100 dark:bg-gray-700"
              />
            ))}
          </div>
        ) : error ? (
          <p className="py-8 text-center text-red-400">
            Couldn’t load the leaderboard. Please try again.
          </p>
        ) : rows.length === 0 ? (
          <p className="py-8 text-center text-gray-400 dark:text-gray-500">
            No entries yet.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-gray-500 dark:text-gray-400">
                <th className="w-12 py-2">#</th>
                <th className="py-2">Name</th>
                {activeTab === "chapters" && <th className="py-2">Tier</th>}
                {activeTab === "teams" && <th className="py-2">Status</th>}
                <th className="py-2 text-right">Score</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-gray-200 dark:border-gray-700 last:border-0"
                >
                  <td className="py-2 font-semibold text-gray-700 dark:text-gray-300">
                    {row.rank}
                  </td>
                  <td className="py-2 text-gray-900 dark:text-gray-100">
                    {row.name}
                  </td>
                  {activeTab === "chapters" && (
                    <td className="py-2">
                      <TierBadge label={row.tier_display} tier={row.tier} />
                    </td>
                  )}
                  {activeTab === "teams" && (
                    <td className="py-2 capitalize text-gray-900 dark:text-gray-100">
                      {row.status_display || row.status}
                    </td>
                  )}
                  <td className="py-2 text-right font-medium text-gray-900 dark:text-gray-100">
                    {row.score}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </DashboardShell>
  );
}
